import axios from 'axios'

// Determine base URL based on environment
const getBaseURL = () => {
  // In production, use relative URLs (same domain as the app)
  if (import.meta.env.PROD) {
    return ''
  }

  // In development, use the API server URL
  return import.meta.env.VITE_API_URL || 'http://localhost:5000'
}

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true // Include credentials for CORS
})

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token from localStorage if available
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method.toUpperCase()} ${config.url}`, config.data || '')
    }

    return config
  },
  (error) => {
    console.error('[API Request Error]', error)
    return Promise.reject(error)
  }
)

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Log response in development
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.method.toUpperCase()} ${response.config.url}`, response.data)
    }

    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Log error in development
    if (import.meta.env.DEV) {
      console.error('[API Error]', error.response?.status, error.response?.data || error.message)
    }

    // Handle 401 Unauthorized - cookie-based auth doesn't use token refresh
    // Just clear any stale localStorage tokens and let the app handle re-authentication
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      // Clear any stale tokens from localStorage (legacy cleanup)
      localStorage.removeItem('auth_token')
      delete apiClient.defaults.headers.common['Authorization']

      // Say what actually happened. Pages report failures with
      // err.response?.data?.message and fall back to their own wording, so a
      // 401 with an empty body reads as "Failed to save settings" and sends
      // the user hunting for a problem with the thing they were saving rather
      // than signing back in.
      if (!error.response.data || typeof error.response.data !== 'object') {
        error.response.data = {}
      }
      if (!error.response.data.message) {
        error.response.data.message = 'Your session has expired. Please sign in again, then retry.'
      }

      // Navigation stays with the router guards; redirecting from here races
      // with in-flight requests during a route change.
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data?.message || 'You do not have permission to access this resource.')
    }

    // Handle 404 Not Found
    if (error.response?.status === 404) {
      console.error('Resource not found:', error.config.url)
    }

    // Handle 500 Internal Server Error
    if (error.response?.status === 500) {
      console.error('Server error:', error.response.data?.message || 'An internal server error occurred.')
    }

    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error.message)
    }

    return Promise.reject(error)
  }
)

// Helper functions for common API calls
export const api = {
  // GET request
  get: (url, config = {}) => apiClient.get(url, config),

  // POST request
  post: (url, data = {}, config = {}) => apiClient.post(url, data, config),

  // PUT request
  put: (url, data = {}, config = {}) => apiClient.put(url, data, config),

  // PATCH request
  patch: (url, data = {}, config = {}) => apiClient.patch(url, data, config),

  // DELETE request
  delete: (url, config = {}) => apiClient.delete(url, config),

  // Upload file with progress tracking
  upload: (url, formData, onProgress) => {
    return apiClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(percentCompleted)
        }
      }
    })
  },

  // Download file
  download: (url, filename) => {
    return apiClient.get(url, {
      responseType: 'blob'
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    })
  }
}

// Stripe API methods
export const stripeApi = {
  // Get current subscription status
  getSubscription: () => {
    return apiClient.get('/api/stripe/subscription')
  },

  // Create a checkout session for subscription
  createCheckoutSession: (billingCycle = 'monthly') => {
    return apiClient.post('/api/stripe/create-checkout-session', { billingCycle })
  },

  // Create a billing portal session
  createPortalSession: () => {
    return apiClient.post('/api/stripe/create-portal-session')
  },

  // Get payment methods
  getPaymentMethods: () => {
    return apiClient.get('/api/stripe/payment-methods')
  },

  // Get invoices
  getInvoices: (limit = 12) => {
    return apiClient.get(`/api/stripe/invoices?limit=${limit}`)
  },

  // Download invoice
  downloadInvoice: (invoiceId) => {
    return apiClient.get(`/api/stripe/invoices/${invoiceId}/download`)
  },

  // Cancel subscription
  cancelSubscription: () => {
    return apiClient.post('/api/stripe/cancel-subscription')
  },

  // Reactivate subscription
  reactivateSubscription: () => {
    return apiClient.post('/api/stripe/reactivate-subscription')
  },

  // Get upcoming invoice
  getUpcomingInvoice: () => {
    return apiClient.get('/api/stripe/upcoming-invoice')
  },

  // Get customer info
  getCustomer: () => {
    return apiClient.get('/api/stripe/customer')
  }
}

// Support API methods
export const supportApi = {
  // Get available topics
  getTopics: () => {
    return apiClient.get('/api/support/topics')
  },

  // Get user's tickets
  getMyTickets: () => {
    return apiClient.get('/api/support/tickets')
  },

  // Get a specific ticket
  getTicket: (ticketId) => {
    return apiClient.get(`/api/support/tickets/${ticketId}`)
  },

  // Create a new ticket
  createTicket: (topic, subject, content) => {
    return apiClient.post('/api/support/tickets', { topic, subject, content })
  },

  // Reply to a ticket
  replyToTicket: (ticketId, content) => {
    return apiClient.post(`/api/support/tickets/${ticketId}/reply`, { content })
  },

  // Mark ticket as solved
  solveTicket: (ticketId) => {
    return apiClient.post(`/api/support/tickets/${ticketId}/solve`)
  }
}

export default apiClient
